import React, { Component } from 'react';
import P from 'prop-types';
import { compose } from 'recompose';
import { withApollo } from 'react-apollo';
import { Redirect, withRouter } from 'react-router-dom';
import LogRocket from 'logrocket';

import { IS_USER_LOGGED_IN } from 'gql/queries';

const BrowserError = ({ error }) => (
  <div>
    <pre>{error.message}</pre>
  </div>
);

BrowserError.propTypes = {
  error: P.instanceOf(Error).isRequired,
};

class ErrorBoundary extends Component {
  static propTypes = {
    children: P.node.isRequired,
  }

  state = {
    error: null,
  }

  componentDidCatch(error) {
    this.setState({ error });
  }

  render() {
    const { error } = this.state;
    if (error) {
      return <BrowserError error={error} />;
    }
    return this.props.children;
  }
}


export default (ChildComponent) => {
  class UserLoggedInWrapper extends Component {
    static propTypes = {
      client: P.shape({
        query: P.func.isRequired,
      }),
      location: P.shape({
        pathname: P.string.isRequired,
      }).isRequired,
    };

    static displayName =
      `UserLoggedInWrapper(${ChildComponent.displayName || ChildComponent.name})`;

    constructor(props) {
      super(props);
      this.state = {
        loading: true,
        isLoggedIn: false,
      };
    }

    async componentDidMount() {
      let user;
      try {
        const { data } = await this.props.client.query({
          query: IS_USER_LOGGED_IN,
        });

        user = data.store.user;
      } catch (e) {
        this.setState({ loading: false });
      }

      // LogRocket
      if (user && user.id) {
        LogRocket.identify(user.id, {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
        });
      }
      // LogRocket Ends

      this.setState({
        loading: false,
        user,
        isLoggedIn: !!(user && user.id),
      });
    }

    render() {
      const { loading, user, isLoggedIn } = this.state;
      const { pathname } = this.props.location;

      const isInPaths = (paths) => {
        return paths.some(path => path === pathname);
      };

      const isVerified = user && user.emailVerified;
      const isInAuthRoute = isInPaths(['/register', '/login']);

      if (loading) {
        return null;
      }
      // User should not be on register or login page when already logged in.
      else if (isLoggedIn && isInAuthRoute) {
        return <Redirect to="/" />;
      }
      // If user is logged in and not verified, and isn't already on the verify page,
      // take them there.
      else if (isLoggedIn && !isVerified && !isInPaths(['/verify-email'])) {
        return <Redirect to="/verify-email" />;
      }
      // If user is not logged in and aren't already in the login/registration page,
      // take them to the login page.
      else if (!isLoggedIn && !isInAuthRoute) {
        return <Redirect to="/login" />;
      }
      return (
        <ErrorBoundary>
          <ChildComponent {...this.props} />
        </ErrorBoundary>
      );
    }
  }

  return compose(
    withApollo,
    withRouter
  )(UserLoggedInWrapper);
};
